/**
 * Read Google Analytics 4 client_id from multiple sources (robust).
 *
 * Priority:
 *   1. gtag('get') API (most reliable when GA4 is loaded)
 *   2. _ga cookie  (GA1.2.{client_id})
 *   3. _ga_* container cookie (GA1.1.{session_id})
 *
 * Used to attach client_id to Shopify cart attributes so the server-side
 * `purchase` event sent via Measurement Protocol can be stitched to the
 * correct GA4 user session.
 */

/** Cached value — avoids re-parsing cookies on every call */
let cachedClientId: string | null = null;

export function getGaClientId(): string | null {
  if (cachedClientId) return cachedClientId;
  if (typeof document === "undefined") return null;

  // Method 1: _ga cookie (most common)
  const gaCookie = document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d\.([^;]+)/);
  if (gaCookie?.[1]) {
    cachedClientId = gaCookie[1];
    return cachedClientId;
  }

  // Method 2: _ga_* container cookies (format: GS2.1.{timestamp}.{count}...)
  // The client_id is in the _ga cookie, but sometimes only _ga_XXXX exists
  // and we can extract the session from it as a partial fallback
  const gaContainerMatch = document.cookie.match(/(?:^|;\s*)_ga_[A-Z0-9]+=GS\d\.\d\.([^;]+)/);
  if (gaContainerMatch?.[1]) {
    // Not a full client_id but better than nothing for session stitching
    cachedClientId = gaContainerMatch[1].split(".").slice(0, 2).join(".");
    if (cachedClientId && cachedClientId.includes(".")) return cachedClientId;
    cachedClientId = null;
  }

  return null;
}

/**
 * Read the GA4 `session_id` from the `_ga_<MEASUREMENT_ID>` container cookie.
 *
 * Cookie format (GA4): `_ga_QGRHD2X1KG=GS2.1.s<session_id>$o<n>$g<n>...`
 * The session_id is the integer after `s` and is what GA4 uses to stitch
 * Measurement Protocol events to the user's browser session. Returns `null`
 * if no `_ga_*` cookie is present (Safari ITP, incognito, pre-GA4 load).
 */
export function getGaSessionId(): string | null {
  if (typeof document === "undefined") return null;
  try {
    const m = document.cookie.match(/(?:^|;\s*)_ga_[A-Z0-9]+=([^;]+)/);
    if (!m?.[1]) return null;
    const value = decodeURIComponent(m[1]);
    // Modern (GA4): GS2.1.s<session_id>$o<n>$g<n>$t<ts>$h<n>
    const m2 = value.match(/[.$]s(\d{8,})\b/);
    if (m2?.[1]) return m2[1];
    // Legacy fallback: GS1.1.<session_id>.<count>...
    const parts = value.split(".");
    if (parts.length >= 3 && /^\d{8,}$/.test(parts[2])) return parts[2];
    return null;
  } catch {
    return null;
  }
}

/**
 * Wait up to `maxMs` for GA4 to create the _ga cookie.
 * Polls every 100ms. Returns client_id or null on timeout.
 */
export function waitForGaClientId(maxMs = 500): Promise<string | null> {
  return new Promise((resolve) => {
    // Immediate check
    const immediate = getGaClientId();
    if (immediate) return resolve(immediate);

    // Also try gtag('get') API if available
    try {
      const gtag = (window as any).gtag;
      if (typeof gtag === "function") {
        // Try to get client_id via gtag API (async callback)
        const measurementIds = extractMeasurementIds();
        for (const mid of measurementIds) {
          gtag("get", mid, "client_id", (clientId: string) => {
            if (clientId && !cachedClientId) {
              cachedClientId = clientId;
              resolve(clientId);
            }
          });
        }
      }
    } catch {
      // gtag not available yet
    }

    const start = Date.now();
    const interval = setInterval(() => {
      const id = getGaClientId();
      if (id) {
        clearInterval(interval);
        resolve(id);
        return;
      }
      if (Date.now() - start >= maxMs) {
        clearInterval(interval);
        resolve(null);
      }
    }, 100);
  });
}

/** Extract GA4 measurement IDs from dataLayer or script tags */
function extractMeasurementIds(): string[] {
  const ids: string[] = [];
  try {
    // Check dataLayer for config commands
    const dl = (window as any).dataLayer;
    if (Array.isArray(dl)) {
      for (const entry of dl) {
        if (Array.isArray(entry) && entry[0] === "config" && typeof entry[1] === "string" && entry[1].startsWith("G-")) {
          ids.push(entry[1]);
        }
      }
    }
    // Check for gtag script src
    const scripts = document.querySelectorAll('script[src*="gtag/js?id=G-"]');
    scripts.forEach((s) => {
      const m = s.getAttribute("src")?.match(/id=(G-[A-Z0-9]+)/);
      if (m) ids.push(m[1]);
    });
  } catch {
    // ignore
  }
  return [...new Set(ids)];
}

/**
 * Reset cached client_id (useful for testing or when consent changes)
 */
export function resetGaClientIdCache(): void {
  cachedClientId = null;
}

/**
 * Append GA4 cross-domain linker parameter `_gl` to a URL.
 *
 * NO-OP — kept as compatibility shim.
 *
 * Why this exists but does nothing: previously we hand-rolled the `_gl`
 * format (`1*1*<name>*<encoded>...`). That format is INCORRECT — Google's
 * real linker uses a checksum + timestamp prefix that we can't replicate
 * client-side without `gtag('get', ..., 'linker', ...)` which is not part
 * of the public API. A malformed `_gl` is silently dropped by GA4 on the
 * destination domain, so we'd lose the client_id stitch we tried to make.
 *
 * The CORRECT mechanism — already configured in GTM container GTM-T8PVRK33:
 *  - GA4 Config tag (id 85) has `linker.domains = [zooi.se, www.zooi.se,
 *    checkout.shopify.com, shop.app, pay.shopify.com]` and `accept_incoming = true`.
 *  - Google-tag AW-18059753112 (id 91) has the same linker config.
 *  - Conversion Linker tag (id 100) fires on All Pages.
 *
 * Together those auto-decorate any outbound link to the listed domains
 * with a valid `_gl` parameter (and an `_aw` for Google Ads). Our
 * `window.location.href = checkoutUrl` triggers the auto-linker.
 *
 * Returns the URL unchanged so callers don't need to be refactored, but
 * we no longer corrupt the URL with a fake parameter.
 */
export function appendGlLinker(url: string): string {
  return url;
}

